import React, { useState, useEffect } from "react";
import "./LowActivityPanel.css";

// Empty in dev -> relative "/api" paths, proxied to the local backend (see frontend/.env.*).
const RAILWAY = process.env.REACT_APP_API_BASE || "";

function formatHireDate(dateStr) {
  if (!dateStr) return "Unknown";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric"
    });
  } catch { return "Unknown"; }
}

function daysSinceHire(dateStr) {
  if (!dateStr) return null;
  const hired = new Date(dateStr);
  const now   = new Date();
  return Math.floor((now - hired) / (1000 * 60 * 60 * 24));
}

const AVATAR_COLORS = [
  ["#1a3a2a","#3ecf8e"],["#1a2a3a","#4d9ef7"],["#2a1a3a","#a78bfa"],
  ["#3a2a1a","#f5a623"],["#2a1a1a","#e05c5c"],["#1a3a3a","#26C6DA"],
];
function avatarColor(id) {
  return AVATAR_COLORS[(id?.charCodeAt(0)||0) % AVATAR_COLORS.length];
}

// Caregivers are external — a plain tel: link is the reliable click-to-call
// (hands the E.164 number to the machine's default calling app), rather than a
// Teams user-call by email or a license-gated Teams PSTN deep link.
function e164(phone) {
  if (!phone) return null;
  const n = String(phone).trim();
  if (n.startsWith("+")) return "+" + n.slice(1).replace(/\D/g, "");
  const d = n.replace(/\D/g, "");
  if (d.length === 10) return "+1" + d;
  if (d.length === 11 && d[0] === "1") return "+" + d;
  return d ? "+" + d : null;
}
function callUrl(phone) {
  const n = e164(phone);
  return n ? `tel:${n}` : null;
}

export default function LowActivityPanel() {
  const [workers, setWorkers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [threshold, setThreshold] = useState(5);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${RAILWAY}/api/workers/low-activity?threshold=${threshold}`)
      .then(r => r.json())
      .then(d => setWorkers(d.workers || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [threshold]);

  return (
    <aside className="low-activity-panel">
      <div className="lap-header">
        <div className="lap-title-row">
          <div>
            <div className="lap-title">⚡ Needs Traction</div>
            <div className="lap-subtitle">Caregivers with fewer than {threshold} total shifts</div>
          </div>
          <select
            className="lap-threshold"
            value={threshold}
            onChange={e => setThreshold(parseInt(e.target.value))}>
            <option value={3}>&lt; 3 shifts</option>
            <option value={5}>&lt; 5 shifts</option>
            <option value={10}>&lt; 10 shifts</option>
            <option value={20}>&lt; 20 shifts</option>
          </select>
        </div>
        {!loading && (
          <div className="lap-count">{workers.length} caregiver{workers.length !== 1 ? "s" : ""} need outreach</div>
        )}
      </div>

      <div className="lap-list">
        {loading && (
          <div className="lap-empty">
            <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }}/>
            <span style={{ marginLeft: 8, fontSize: 12 }}>Loading…</span>
          </div>
        )}
        {error && (
          <div className="lap-empty" style={{ color: "#e05c5c", fontSize: 11 }}>
            ⚠ {error}
          </div>
        )}
        {!loading && !error && workers.length === 0 && (
          <div className="lap-empty">
            <div style={{ fontSize: 22, marginBottom: 6 }}>✓</div>
            <p>All caregivers have {threshold}+ shifts</p>
          </div>
        )}
        {!loading && workers.map((w, i) => {
          const [bg, fg] = avatarColor(w.id);
          const days = daysSinceHire(w.hireDate);
          const isNew = days !== null && days <= 90;
          return (
            <div key={w.id} className={`lap-card ${isNew ? "lap-card-new" : ""}`}>
              <div className="lap-rank">{i + 1}</div>
              <div className="lap-avatar" style={{ background: bg, color: fg }}>
                {(w.firstName||"?")[0]}{(w.lastName||"?")[0]}
                {isNew && <span className="lap-new-dot"/>}
              </div>
              <div className="lap-info">
                <div className="lap-name">
                  {w.firstName} {w.lastName}
                  {isNew && <span className="lap-new-badge">NEW</span>}
                </div>
                <div className="lap-role-line">
                  {w.role && <span className={`role-chip role-${w.role.toLowerCase()}`}>{w.role}</span>}
                  {w.phone && <span className="lap-phone">{w.phone}</span>}
                </div>
                <div className="lap-meta">
                  <span className="lap-hire">
                    Hired {formatHireDate(w.hireDate)}
                    {days !== null && <span className="lap-days"> ({days}d ago)</span>}
                  </span>
                </div>
              </div>
              <div className="lap-right">
                <div className="lap-shift-count">
                  <span className="lap-shift-number">{w.shiftCount ?? 0}</span>
                  <span className="lap-shift-label">shifts</span>
                </div>
                {callUrl(w.phone) && (
                  <a className="lap-call-btn"
                    href={callUrl(w.phone)}
                    title={`Call ${w.phone}`}>
                    📞
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
}
